/**
 * The wire contract between the browser app and the hosted API.
 *
 * This file is imported by *both* sides — `src/cloud/*` in the SPA and `server/src/routes/*` — so a
 * route and its caller cannot drift without the type-check failing. It deliberately contains types
 * and constants only: no runtime behaviour, nothing DOM- or Node-specific. That is what lets one
 * module compile under the browser tsconfig and the server tsconfig at once.
 *
 * The one import is a type-only import from `./plans.js`, which is subject to the same rules and
 * emits nothing. The `.js` specifier is required by the server's NodeNext resolution and is
 * understood by the browser's bundler resolution; every cross-import inside `shared/` is written
 * that way.
 *
 * Project documents travel as `unknown` here on purpose. The authority on a project's shape is
 * `src/project/ProjectSerializer`, which the client already runs on every read; making the server
 * re-declare that 700-line schema would create a second source of truth that silently rots. The
 * server treats a document as opaque JSON it stores per user, and validates size and ownership
 * rather than botany.
 */

import type { PlanId } from './plans.js';

/** Path prefix for every authenticated JSON route. */
export const API_PREFIX = '/api';

/** Path prefix for the OAuth handshake, which is browser-navigated rather than fetched. */
export const AUTH_PREFIX = '/auth';

/** Name of the httpOnly session cookie. Referenced by the server only; here for documentation. */
export const SESSION_COOKIE = 'glade_session';

// ---------------------------------------------------------------------------
// Identity
// ---------------------------------------------------------------------------

export interface CloudUser {
  id: string;
  email: string;
  /** Display name from the Google profile, or the email local-part when Google sends none. */
  name: string;
  /** Google-hosted avatar URL, absent when the account has no picture. */
  pictureUrl?: string;
  createdAt: string;
}

export interface SessionResponse {
  /** Null when the caller is anonymous — this route never 401s, so the SPA can boot either way. */
  user: CloudUser | null;
  /** Whether the deployment has Google OAuth configured at all. False for a keyless local run. */
  authAvailable: boolean;
  /**
   * Whether the app refuses anonymous use entirely.
   *
   * True means the SPA must show a sign-in screen rather than the editor. It is reported here
   * rather than assumed, so one build serves both a gated SaaS and the standalone tool.
   */
  requireAuth: boolean;
  /** Whether the operator supplied a platform OpenAI key, so AI works without the user's own. */
  platformAiAvailable: boolean;
  /**
   * Whether this caller may reach the operator console.
   *
   * Reported so the SPA can show a link to `/admin` rather than leaving it to be typed from memory.
   * It is not the access control — the server checks `ADMIN_EMAILS` on every admin route, and a
   * client that set this to true for itself would gain a menu item and four 404s.
   */
  isAdmin: boolean;
  /** Present only for a signed-in user. */
  usage?: UsageSummary;
}

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------

/**
 * The counts the library screen shows for each saved site.
 *
 * Computed by the client at save time (`summarizeProject`) and stored denormalized beside the
 * document. That looks like duplication, and it is — bought deliberately, because the alternative
 * is the library screen downloading every document to count the plants in it. The counts are
 * refreshed on every write, so they cannot drift from the document they describe by more than the
 * one save that is in flight.
 */
export interface ProjectStats {
  plantCount: number;
  speciesCount: number;
  groupCount: number;
  siteObjectCount: number;
  /**
   * Placed creatures and figures. Optional because a listing written before fauna existed carries no
   * such field, and a library screen must render a row from an older server rather than refuse it.
   */
  faunaCount?: number;
  tagCount: number;
  templateId: string;
  widthMeters: number;
  lengthMeters: number;
}

/** A project in a listing: metadata only, so the library renders without shipping every document. */
export interface CloudProjectSummary {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  /** Serialized document size, so the UI can show what an account is holding. */
  sizeBytes: number;
  /** Null for a row written before stats were recorded. The UI degrades to showing dates only. */
  stats: ProjectStats | null;
  /** Whether this site was handed to the account at signup rather than made in it. */
  isStarter: boolean;
}

export interface ProjectListResponse {
  projects: CloudProjectSummary[];
  /**
   * The site to open on a first visit, or null.
   *
   * Non-null only while the account has made nothing of its own — the server answers the whole
   * question, so the client never has to work out what "your first visit" means. It goes null the
   * moment the user saves a site of their own and stays that way.
   */
  opensFirst: string | null;
  /**
   * The site to reopen on a return visit, or null.
   *
   * The most recently opened project in the account. Held here rather than derived from
   * `projects[0]` because that list is ordered by `updatedAt`, and the site someone last *edited*
   * is not always the one they last *had open* -- opening a site to look at it changes nothing.
   *
   * Takes precedence over {@link ProjectListResponse.opensFirst}: a starter is what to show
   * someone who has never been here, and this is what to show someone who has.
   */
  resumeId: string | null;
}

export interface ProjectResponse {
  project: CloudProjectSummary;
  /** The stored `LandscapeProject`, opaque to the server. */
  document: unknown;
}

export interface ProjectSaveRequest {
  /** Client-generated project id. Scoped per user, so two accounts may hold the same id. */
  id: string;
  name: string;
  document: unknown;
  stats?: ProjectStats;
}

/** Body of a rename, which rewrites the name without resending the document. */
export interface ProjectRenameRequest {
  name: string;
}

// ---------------------------------------------------------------------------
// Photos
// ---------------------------------------------------------------------------

/**
 * The non-binary half of a stored shot.
 *
 * `StoredShot` in `src/capture/PhotoStore` carries four Blobs plus this metadata. The four images
 * go to object storage and come back as URLs; everything else round-trips as JSON. `manifest`,
 * `camera`, and `environment` stay opaque for the same reason project documents do.
 */
export interface PhotoSummary {
  id: string;
  createdAt: number;
  title: string;
  /** Pixel dimensions of the capture, mirroring the client's `ImageSize`. */
  size: PhotoDimensions;
  manifest: unknown;
  camera: unknown;
  environment: unknown;
  variants: PhotoVariantSummary[];
  /** Signed or same-origin URLs for the four passes. */
  images: {
    beauty: string;
    idMap: string;
    depth: string;
    thumbnail: string;
  };
}

export interface PhotoVariantSummary {
  id: string;
  createdAt: number;
  presetId: string;
  presetLabel: string;
  prompt: string;
  model: string;
  quality: string;
  size: PhotoDimensions;
  estimatedCostUsd: number;
  imageUrl: string;
}

/** Pixel dimensions of a capture or a rendered variant. */
export interface PhotoDimensions {
  width: number;
  height: number;
}

export interface PhotoListResponse {
  photos: PhotoSummary[];
  usageBytes: number;
}

/** JSON part of the multipart photo upload; the binaries ride alongside as named file parts. */
export interface PhotoCreateMeta {
  title: string;
  size: PhotoDimensions;
  manifest: unknown;
  camera: unknown;
  environment: unknown;
}

/** JSON part of a variant upload. */
export interface PhotoVariantCreateMeta {
  presetId: string;
  presetLabel: string;
  prompt: string;
  model: string;
  quality: string;
  size: PhotoDimensions;
  estimatedCostUsd: number;
}

// ---------------------------------------------------------------------------
// AI proxy
// ---------------------------------------------------------------------------

/**
 * The AI operations the browser may ask the server to perform on its behalf.
 *
 * Re-exported rather than declared, because the endpoint, the allow-list and the default model for
 * each one all live together in `shared/ai/models.ts` — and a second declaration of the union here
 * is exactly the kind of near-duplicate that lets a new operation reach one half of the app.
 */
export type { AiOperation } from './ai/models.js';

/**
 * What this account has spent this month, and what it is allowed.
 *
 * Two units, on purpose. **Credits are what the user is shown** — they are comparable across
 * features, which a call count is not, since a wand cast and a high-quality enhanced render differ
 * by roughly 330× in what they cost to serve. `used` and `limit` are the call count, kept because
 * the operator view in phase 5 wants "how many calls" as a separate question from "how much", and
 * because dropping them would break every existing client mid-deploy.
 *
 * Enforcement is on credits. The call count is now reporting only.
 */
export interface UsageSummary {
  /** Calendar-month window the counts cover, as `YYYY-MM`. */
  period: string;
  /** AI calls made this period against the platform key. Own-key calls are not counted. */
  used: number;
  /** Calls allowed per period on the platform key. Null means unmetered. */
  limit: number | null;
  /** Credits spent this period on the platform key — the figure the UI shows. */
  creditsUsed: number;
  /** Credits allowed per period. Null means unmetered. */
  creditsLimit: number | null;
  /**
   * The plan this account is on, as an id the client resolves against `shared/plans.ts`.
   *
   * The id rather than the resolved definition, because the definitions ship in the bundle: sending
   * the price, the project limit and the export quality over the wire would let a stale tab quote
   * one set of terms while the server enforces another. The id is the only part that is genuinely
   * per-account.
   */
  plan: PlanId;
  /** Whether this account has supplied its own OpenAI key. */
  hasOwnKey: boolean;
  /** Redacted form of the stored key, e.g. `sk-…a91f`. */
  ownKeyHint?: string;
  /**
   * Unexpired credit grants on this account, newest first.
   *
   * Sent as rows rather than folded into {@link creditsLimit} because the two answer different
   * questions. The limit says what the account may spend this month; a grant says *why* it is more
   * than the plan's figure and — the part that matters — **when that stops being true**. An account
   * given 500 credits that expire in a fortnight is in a materially different position from one on
   * a standing arrangement, and a single summed number cannot tell the user which they are.
   *
   * Empty on the common account, which is the point: no grants, no section.
   *
   * Optional for the same reason `ProjectStats.creatureCount` is: a tab left open across the deploy
   * that added this field is talking to a server that never sends it, and "absent" and "none" both
   * render nothing, so there is no state the client loses by treating them alike.
   */
  grants?: CreditGrantSummary[];
}

/**
 * One unexpired credit grant, as the account panel shows it.
 *
 * Deliberately not the whole row. `granted_by` is an operator's user id and is nobody's business on
 * the client, and the grant's id buys nothing while there is no UI that acts on a single grant.
 */
export interface CreditGrantSummary {
  /** Credits this grant adds to the monthly allowance. */
  credits: number;
  /**
   * ISO-8601 instant the grant lapses, or null when it never does.
   *
   * Null is not "already expired" — it is a standing arrangement that raises the allowance every
   * month until somebody cancels it, which is how a paid plan is honoured by hand before billing
   * exists. The UI has to say those two things differently.
   */
  expiresAt: string | null;
  /**
   * Why it was granted, in the operator's words.
   *
   * Shown to the user unedited. It is written in the admin console with the knowledge that the
   * account holder reads it, which is a useful constraint on what gets typed there.
   */
  reason: string;
}

export interface OwnKeyRequest {
  /** A full OpenAI key to store, or the empty string to forget the stored one. */
  apiKey: string;
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/**
 * Every non-2xx JSON response carries this shape.
 *
 * `code` exists so the client can branch on a machine-readable reason — the quota wall in
 * particular has to be told apart from a generic 403 to show the right panel — while `message`
 * stays the thing a user reads.
 */
export interface ApiErrorBody {
  error: {
    code: ApiErrorCode;
    message: string;
    /** Present on 429s, in seconds. */
    retryAfter?: number;
  };
}

export type ApiErrorCode =
  | 'unauthenticated'
  | 'forbidden'
  | 'not_found'
  | 'invalid_request'
  | 'payload_too_large'
  | 'quota_exceeded'
  /** The plan's ceiling on saved sites, hit while creating one more. */
  | 'project_limit'
  /** The unadvertised safety ceiling on what one account may cost to serve in a month. */
  | 'cost_cap'
  /** An operator has turned AI off for this one account. */
  | 'account_suspended'
  | 'rate_limited'
  | 'upstream_error'
  | 'ai_unavailable'
  | 'internal';

/**
 * Refusals whose `message` is already the sentence the user should read.
 *
 * These four are decisions this app made about this account — out of credits, over the cost cap,
 * suspended, AI switched off — and in every case the server has written a message naming the state
 * and the way out of it. A client that falls through to its own per-status text replaces that with
 * something worse: all four arrive as a 429 or a 403, where the generic advice is "wait and retry"
 * or "check that your API key has this model enabled", and on a hosted deployment the user has no
 * API key and retrying is precisely what does not help.
 *
 * So the rule is: if the code is in here, show the server's message and do not retry. Typed as
 * `readonly string[]` rather than `readonly ApiErrorCode[]` so a client can test a code it parsed
 * out of a body — which may have come from OpenAI rather than from us — without a cast.
 */
export const SERVER_REFUSAL_CODES: readonly string[] = [
  'quota_exceeded',
  'cost_cap',
  'account_suspended',
  'ai_unavailable',
];

/**
 * Every code whose `message` is the sentence to show, AI paths and otherwise.
 *
 * The superset exists because the reasoning is not specific to AI. Two more refusals write a
 * message the client cannot improve on: a library that is full names the plan, the count and both
 * ways out, and a document over the size limit names both sizes in megabytes. Both used to be
 * flattened — one into "you do not have access", the other into "see the console for details" —
 * which is how a save failure ends up telling a user nothing about the thing they must fix.
 */
export const EXPLAINED_ERROR_CODES: readonly string[] = [
  ...SERVER_REFUSAL_CODES,
  'project_limit',
  'payload_too_large',
];
